import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import ServerCostMeter from './ServerCostMeter';
import { fetchFundingStats } from '../../services/fundingService';
import { playBassSlide } from '../../utils/uiSound';

/* ═══════════════════════════════════════════════════════════════════
   DONATE MODAL — Lightbox overlay with multi-gateway support
   Gateways: Stripe (Credit Card), PayPal, Revolut
   
   Payment config:
   ═══════════════════════════════════════════════════════════════════ */

// ─── PAYMENT CONFIGURATION ───────────────────────────────────────
const PAYMENT_CONFIG = {
  stripe: {
    // Stripe Payment Link — "WYMR - Who You Makin' Rich?" (customer chooses amount)
    paymentLink: 'https://donate.stripe.com/6oU4gA5Oc53g9EWcZn8Zq00',
    enabled: true,
  },
  paypal: {
    // PayPal NCP (No-Code Payment) donate button link
    paymentLink: 'https://www.paypal.com/ncp/payment/FKFN7XY5L6M4Q',
    hostedButtonId: 'FKFN7XY5L6M4Q',
    enabled: true,
  },
  revolut: {
    // Your Revolut.me link
    revolutMe: 'https://revolut.me/lawalker',
    enabled: true,
  },
};

const SITE_URL = 'https://whoyoumakinrich.com';
const MERCH_STORE_URL = 'https://merch.whoareyoumakingrich.com';
const PLAY_STORE_URL = '#';
const APP_STORE_URL = '#';

const PRESETS = [0.50, 1, 3, 5, 10, 15];

// ─── MERCH PRODUCTS ────────────────────────────────────────────
const MERCH_PRODUCTS = [
  { name: '$WYMR? — The Original Ringer Tee', desc: 'Unisex Ringer Tee', price: '$19.57', img: '/assets/images/wymr-tee.jpg', url: `${MERCH_STORE_URL}/product/26908769` },
  { name: '$WYMR? — Unisex Tee', desc: 'Classic Unisex Tee', price: '$17.41', img: '/assets/images/wymr-unisex-tee.jpg', url: `${MERCH_STORE_URL}/product/26932090` },
  { name: '$WYMR? — Unisex Tee (Alt)', desc: 'Classic Unisex Tee · Variant', price: '$17.41', img: '/assets/images/wymr-unisex-tee-2.jpg', url: `${MERCH_STORE_URL}/product/26929329` },
  { name: '$WYMR? — Hoodie', desc: 'Cozy Pullover Hoodie', price: '$32.75', img: '/assets/images/wymr-hoodie.jpg', url: `${MERCH_STORE_URL}/product/26928864` },
  { name: '$WYMR? — Baseball Tee', desc: 'Unisex Baseball Tee', price: '$24.29', img: '/assets/images/wymr-baseball-tee.jpg', url: `${MERCH_STORE_URL}/product/26930016` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 1', price: '$66.19', img: '/assets/images/wymr-puffer.jpg', url: `${MERCH_STORE_URL}/product/26930618` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 2', price: '$66.19', img: '/assets/images/wymr-puffer-2.jpg', url: `${MERCH_STORE_URL}/product/26930658` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 3', price: '$66.19', img: '/assets/images/wymr-puffer-3.jpg', url: `${MERCH_STORE_URL}/product/26930674` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 4', price: '$66.19', img: '/assets/images/wymr-puffer-4.jpg', url: `${MERCH_STORE_URL}/product/26931013` },
  { name: '$WYMR? — Puffer Jacket', desc: 'Statement Puffer · Style 5', price: '$66.19', img: '/assets/images/wymr-puffer-5.jpg', url: `${MERCH_STORE_URL}/product/26931092` },
  { name: '$WYMR? — Snapback Cap', desc: 'Snapback Trucker', price: '$23.38', img: '/assets/images/wymr-snapback.jpg', url: `${MERCH_STORE_URL}/product/26932202` },
  { name: '$WYMR? — Trucker Cap', desc: 'Trucker Cap · Side View', price: '$16.59', img: '/assets/images/wymr-trucker-1.jpg', url: `${MERCH_STORE_URL}/product/26932476` },
  { name: '$WYMR? — Trucker Cap', desc: 'Trucker Cap · Front View', price: '$16.59', img: '/assets/images/wymr-trucker-2.jpg', url: `${MERCH_STORE_URL}/product/26932682` },
  { name: '$WYMR? — Racerback Tank', desc: "Women's Racerback Tank", price: '$23.33', img: '/assets/images/wymr-racerback.jpg', url: `${MERCH_STORE_URL}/product/26929902` },
  { name: '$WYMR? — Tank Top', desc: "Women's Spaghetti Strap", price: '$33.92', img: '/assets/images/wymr-tank-top.jpg', url: `${MERCH_STORE_URL}/product/26929798` },
];

const GATEWAYS = [
  {
    id: 'revolut',
    label: 'Revolut',
    icon: '🏦',
    color: 'from-[#0075EB] to-[#0052CC]',
    ring: 'ring-[#0075EB]/30',
    description: 'Bank transfer via Revolut',
  },
  {
    id: 'stripe',
    label: 'Credit Card',
    icon: '💳',
    color: 'from-[#635BFF] to-[#4F46E5]',
    ring: 'ring-[#635BFF]/30',
    description: 'Visa, Mastercard, Amex',
  },
  {
    id: 'paypal',
    label: 'PayPal',
    icon: 'paypal',
    color: 'from-[#003087] to-[#001C64]',
    ring: 'ring-[#003087]/30',
    description: 'PayPal account or guest',
  },
];

const MERCH_ONE_PURCHASE_MSG =
  'Please only purchase one piece of merch to support & help with the server costs. DO NOT use the merch store for shopping. Why—because SAVE MONEY, that\'s why! One purchase, 🤘 rock the merch. The promotion also helps.🙏';

const MERCH_MARQUEE_GAP_PX = 48;
const MERCH_MARQUEE_SPEED_PX_PER_SEC = 110;

const ScrollingMerchNotice = () => {
  const firstCopyRef = useRef(null);
  const [marqueeStyle, setMarqueeStyle] = useState({
    '--marquee-distance': '-50%',
    '--marquee-duration': '18s',
  });

  useEffect(() => {
    const measure = () => {
      const firstCopy = firstCopyRef.current;
      if (!firstCopy) return;

      const segmentWidth = firstCopy.offsetWidth + MERCH_MARQUEE_GAP_PX;
      const durationSec = Math.max(segmentWidth / MERCH_MARQUEE_SPEED_PX_PER_SEC, 10);

      setMarqueeStyle({
        '--marquee-distance': `-${segmentWidth}px`,
        '--marquee-duration': `${durationSec.toFixed(2)}s`,
      });
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <div className="overflow-hidden w-full mt-1.5" aria-live="polite">
      <div
        className="merch-marquee-track flex w-max whitespace-nowrap text-xs text-gray-500"
        style={marqueeStyle}
      >
        <span ref={firstCopyRef} className="inline-block shrink-0" style={{ paddingRight: MERCH_MARQUEE_GAP_PX }}>
          {MERCH_ONE_PURCHASE_MSG}
        </span>
        <span className="inline-block shrink-0" style={{ paddingRight: MERCH_MARQUEE_GAP_PX }} aria-hidden="true">
          {MERCH_ONE_PURCHASE_MSG}
        </span>
      </div>
    </div>
  );
};

const MerchProfitsNotice = () => (
  <div className="mb-3">
    <p className="text-center text-xs text-gray-500">
      100% of profits fund the platform
    </p>
    <ScrollingMerchNotice />
  </div>
);

const SHARE_PLATFORMS = [
  { id: 'x', label: 'X', color: 'bg-black' },
  { id: 'facebook', label: 'Facebook', color: 'bg-[#1877F2]' },
  { id: 'reddit', label: 'Reddit', color: 'bg-[#FF4500]' },
  { id: 'instagram', label: 'Instagram', color: 'bg-[#f09433]' },
  { id: 'tiktok', label: 'TikTok', color: 'bg-[#000000]' },
  { id: 'youtube', label: 'YouTube', color: 'bg-[#FF0000]' },
  { id: 'whatsapp', label: 'WhatsApp', color: 'bg-[#25D366]' },
  { id: 'discord', label: 'Discord', color: 'bg-[#5865F2]' },
  { id: 'telegram', label: 'Telegram', color: 'bg-[#0088cc]' },
  { id: 'email', label: 'Email', color: 'bg-gray-600' },
  { id: 'copy-link', label: 'Copy Link', color: 'bg-gray-600' },
];

function getShareUrl(platform, amount) {
  const text = encodeURIComponent(
    `I just donated $${amount.toFixed(2)} to keep @WhoYouMakinRich running — a free platform exposing who's really making money from your spending. 💰🔍`
  );
  const url = encodeURIComponent(SITE_URL);
  const fullText = encodeURIComponent(
    `I just donated $${amount.toFixed(2)} to keep Who You Makin' Rich running!\n\n🌐 ${SITE_URL}\n📱 Android: ${PLAY_STORE_URL}\n🍎 iOS: ${APP_STORE_URL}`
  );
  switch (platform) {
    case 'x': return `https://x.com/intent/post?text=${text}&url=${url}`;
    case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
    case 'whatsapp': return `https://wa.me/?text=${fullText}`;
    case 'linkedin': return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    case 'reddit': return `https://www.reddit.com/submit?url=${url}&title=${text}`;
    case 'email': return `mailto:?subject=${encodeURIComponent("I donated to Who You Makin' Rich!")}&body=${fullText}`;
    default: return '#';
  }
}

/* ─── SWIPEABLE PRODUCT CAROUSEL ──────────────────────────────────── */

const ProductCarousel = ({ products }) => {
  const [current, setCurrent] = useState(0);
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  const containerRef = useRef(null);
  const minSwipeDistance = 40;

  const handleTouchStart = (e) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEnd.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;
    const distance = touchStart.current - touchEnd.current;
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0 && current < products.length - 1) {
        setCurrent((p) => p + 1);
      } else if (distance < 0 && current > 0) {
        setCurrent((p) => p - 1);
      }
    }
  };

  // Mouse drag for desktop
  const mouseStart = useRef(null);
  const handleMouseDown = (e) => { mouseStart.current = e.clientX; };
  const handleMouseUp = (e) => {
    if (mouseStart.current === null) return;
    const distance = mouseStart.current - e.clientX;
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0 && current < products.length - 1) setCurrent((p) => p + 1);
      else if (distance < 0 && current > 0) setCurrent((p) => p - 1);
    }
    mouseStart.current = null;
  };

  const product = products[current];

  // Track if user swiped so we don't trigger link on swipe
  const didSwipe = useRef(false);

  const wrappedTouchEnd = () => {
    if (touchStart.current && touchEnd.current) {
      const d = Math.abs(touchStart.current - touchEnd.current);
      didSwipe.current = d >= minSwipeDistance;
    }
    handleTouchEnd();
  };

  const wrappedMouseUp = (e) => {
    if (mouseStart.current !== null) {
      const d = Math.abs(mouseStart.current - e.clientX);
      didSwipe.current = d >= 5;
    }
    handleMouseUp(e);
  };

  const handleCardClick = () => {
    if (!didSwipe.current && product.url) {
      window.open(product.url, '_blank', 'noopener,noreferrer');
    }
    didSwipe.current = false;
  };

  return (
    <div className="mb-4">
      <div
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={wrappedTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={wrappedMouseUp}
        onClick={handleCardClick}
        className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200/60 p-3.5 cursor-grab active:cursor-grabbing select-none hover:border-amber-300 transition-colors"
      >
        <div className="flex items-center gap-3.5">
          <div className="w-20 h-20 rounded-lg flex-shrink-0 shadow-md bg-white overflow-hidden">
            <img
              src={product.img}
              alt={product.name}
              className="w-full h-full object-cover transition-opacity duration-300"
              draggable={false}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-900 font-bold text-sm truncate">{product.name}</p>
            <p className="text-gray-500 text-[11px] mt-0.5">{product.desc}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-amber-600 font-bold text-sm">From {product.price}</span>
              <span className="text-[9px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">FREE shipping*</span>
            </div>
            <p className="text-amber-500 text-[10px] font-semibold mt-1.5 hover:text-amber-600 transition-colors">
              View in Store →
            </p>
          </div>
        </div>
      </div>

      {/* Dots + swipe hint */}
      <div className="flex items-center justify-center gap-1.5 mt-2.5">
        {products.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`rounded-full transition-all duration-200 ${
              i === current
                ? 'w-5 h-1.5 bg-amber-500'
                : 'w-1.5 h-1.5 bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
      <p className="text-center text-[9px] text-gray-400 mt-1">
        ← Swipe to browse {products.length} products →
      </p>
    </div>
  );
};

/* ─── MAIN MODAL COMPONENT ───────────────────────────────────────── */

const DonateModal = ({ isOpen, onClose, focusMerch = false }) => {
  const [amount, setAmount] = useState(1);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState('paypal');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['tiktok', 'instagram', 'facebook', 'whatsapp']);
  const [step, setStep] = useState('amount'); // 'amount' | 'pay' | 'waiting' | 'success' | 'merch'
  const [fundingStats, setFundingStats] = useState({
    goal: 141.6,
    raised: 0,
    progress: 0,
    month: new Date().toISOString().slice(0, 7),
  });
  const [fundingLoading, setFundingLoading] = useState(true);
  const [waitingStatus, setWaitingStatus] = useState('polling'); // polling | timeout
  const [baselineRaised, setBaselineRaised] = useState(0);
  const backdropRef = useRef(null);
  const pollRef = useRef(null);

  const effectiveAmount = isCustom ? parseFloat(customAmount) || 0 : amount;
  const isValidAmount = effectiveAmount >= 0.50;

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Reset state when opening — if focusMerch, jump to merch step
  useEffect(() => {
    if (isOpen) {
      playBassSlide();
      setStep(focusMerch ? 'merch' : 'amount');
      setAmount(5);
      setIsCustom(false);
      setCustomAmount('');
      setFundingLoading(true);
      setWaitingStatus('polling');
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      fetchFundingStats().then((stats) => {
        setFundingStats(stats);
        setFundingLoading(false);
      });
    }
  }, [isOpen, focusMerch]);

  // Stop polling when modal closes or leaves waiting step
  useEffect(() => {
    if (!isOpen || step !== 'waiting') {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }, [isOpen, step]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  const togglePlatform = (id) => {
    setSelectedPlatforms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleProceedToPayment = () => {
    if (!isValidAmount) return;
    setStep('pay');
  };

  const startPaymentPolling = (baseline) => {
    if (pollRef.current) clearInterval(pollRef.current);
    setWaitingStatus('polling');
    const startedAt = Date.now();
    const maxMs = 2 * 60 * 1000;

    pollRef.current = setInterval(async () => {
      const stats = await fetchFundingStats();
      setFundingStats(stats);
      if (stats.raised > baseline + 0.001) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setStep('success');
        return;
      }
      if (Date.now() - startedAt >= maxMs) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setWaitingStatus('timeout');
      }
    }, 3000);
  };

  const handlePayNow = () => {
    const config = PAYMENT_CONFIG[selectedGateway];
    let paymentUrl = null;

    switch (selectedGateway) {
      case 'stripe':
        if (config.paymentLink) paymentUrl = config.paymentLink;
        break;
      case 'paypal':
        if (config.paymentLink) paymentUrl = config.paymentLink;
        break;
      case 'revolut':
        if (config.revolutMe) paymentUrl = config.revolutMe;
        break;
    }

    if (paymentUrl) {
      window.open(paymentUrl, '_blank', 'noopener,noreferrer');
    }

    const baseline = fundingStats.raised;
    setBaselineRaised(baseline);
    setStep('waiting');
    startPaymentPolling(baseline);
  };

  const handleKeepChecking = () => {
    startPaymentPolling(baselineRaised || fundingStats.raised);
  };

  const handleShareAll = () => {
    for (const platform of selectedPlatforms) {
      const url = getShareUrl(platform, effectiveAmount);
      window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');
    }
  };

  // ─── RENDER ───────────────────────────────────────────────────────

  const isMerchSheet = step === 'merch';

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className={`fixed inset-0 z-[100] flex justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 ${
        isMerchSheet ? 'items-end p-0 sm:p-4' : 'items-center p-4'
      }`}
    >
      <div
        className={`relative w-full max-w-md overflow-y-auto bg-white shadow-2xl border border-gray-200 ${
          isMerchSheet
            ? 'max-h-[88vh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl border-b-0 sm:border-b'
            : 'max-h-[90vh] rounded-2xl'
        }`}
        style={{ animation: isMerchSheet ? 'modalSlideUpBottom 0.3s ease-out' : 'modalSlideUp 0.25s ease-out' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
        >
          <Icon name="X" size={16} />
        </button>

        {/* ═══ STEP 1: Choose Amount ═══ */}
        {step === 'amount' && (
          <div className="p-6">
            {/* Header */}
            <div className="text-center mb-5">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center shadow-lg">
                <Icon name="Heart" size={22} className="text-white" />
              </div>
              <h2 className="font-heading text-xl font-bold text-gray-900">
                Help Keep Transparency Alive
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                100% goes to servers, research & data
              </p>
            </div>

            <ServerCostMeter
              goal={fundingStats.goal}
              raised={fundingStats.raised}
              loading={fundingLoading}
              month={fundingStats.month}
            />

            {/* Amount grid */}
            <label className="block text-xs font-semibold text-gray-600 mb-2">Choose an amount</label>
            <div className="grid grid-cols-3 gap-1.5 mb-3">
              {PRESETS.map(preset => (
                <button
                  key={preset}
                  onClick={() => { setAmount(preset); setIsCustom(false); }}
                  className={`py-2.5 rounded-lg text-sm font-bold transition-all border-2 ${
                    !isCustom && amount === preset
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-primary/40'
                  }`}
                >
                  ${preset < 1 ? preset.toFixed(2) : preset}
                </button>
              ))}
            </div>

            {/* Custom amount */}
            <button
              onClick={() => setIsCustom(true)}
              className={`w-full text-left px-3 py-2.5 rounded-lg border-2 transition-all mb-4 ${
                isCustom ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {isCustom ? (
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-primary">$</span>
                  <input
                    type="number"
                    min="0.50"
                    step="0.01"
                    placeholder="Custom amount (min $0.50)"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-gray-900 font-medium text-sm"
                    autoFocus
                  />
                </div>
              ) : (
                <span className="text-xs text-gray-500">Or enter a custom amount...</span>
              )}
            </button>

            {/* Inspirational message */}
            <p className="text-center text-sm text-gray-600 font-medium leading-relaxed mb-4">
              Donate or grab some merch — both help keep the servers and services up and running!
            </p>

            {/* Validation */}
            {isCustom && customAmount && !isValidAmount && (
              <div className="flex items-center gap-1.5 text-red-500 text-xs mb-3">
                <Icon name="AlertCircle" size={13} />
                <span>Minimum donation is $0.50</span>
              </div>
            )}

            {/* Continue button */}
            <button
              onClick={handleProceedToPayment}
              disabled={!isValidAmount}
              className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:from-emerald-400 hover:to-cyan-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              <Icon name="ArrowRight" size={16} />
              Continue — ${isValidAmount ? effectiveAmount.toFixed(2) : '—'}
            </button>

            {/* Trust */}
            <div className="flex items-center justify-center gap-4 mt-3 text-[9px] text-gray-400">
              <span className="flex items-center gap-1"><Icon name="Shield" size={10} className="text-green-400" /> SSL Encrypted</span>
              <span className="flex items-center gap-1"><Icon name="Lock" size={10} className="text-blue-400" /> Secure</span>
              <span className="flex items-center gap-1"><Icon name="Heart" size={10} className="text-red-400" /> 100% to Servers</span>
            </div>

            {/* ── "Or" divider ── */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Swipeable product carousel */}
            <ProductCarousel products={MERCH_PRODUCTS} />
            <a
              href={MERCH_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:from-amber-400 hover:to-orange-500 transition-all flex items-center justify-center gap-2 text-sm"
            >
              <Icon name="ShoppingBag" size={16} />
              Grab Some Merch
            </a>

            {/* Tagline */}
            <p className="text-center text-[11px] text-gray-400 mt-3">
              Both help keep the servers and services running 🙌🤟🙏
            </p>
          </div>
        )}

        {/* ═══ STEP 2: Choose Payment Gateway ═══ */}
        {step === 'pay' && (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
              <button
                onClick={() => setStep('amount')}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
              >
                <Icon name="ArrowLeft" size={14} />
              </button>
              <div>
                <h2 className="font-heading text-lg font-bold text-gray-900">
                  Choose Payment Method
                </h2>
                <p className="text-xs text-gray-500">
                  Donating <span className="font-bold text-emerald-600">${effectiveAmount.toFixed(2)}</span>
                </p>
              </div>
            </div>

            {/* Gateway options */}
            <div className="space-y-2 mb-5">
              {GATEWAYS.filter(g => PAYMENT_CONFIG[g.id]?.enabled).map(gw => (
                <button
                  key={gw.id}
                  onClick={() => setSelectedGateway(gw.id)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all text-left ${
                    selectedGateway === gw.id
                      ? `border-transparent ring-2 ${gw.ring} bg-gradient-to-r ${gw.color} text-white shadow-md`
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-2xl">
                    {gw.icon === 'paypal' ? (
                      <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.56A.77.77 0 0 1 5.704 2h5.836c2.137 0 3.788.613 4.508 1.92.31.563.46 1.18.437 1.846-.024.684-.227 1.5-.604 2.43-.854 2.11-2.675 3.183-5.415 3.183H8.397a.77.77 0 0 0-.76.638l-1.06 5.586a.641.641 0 0 1-.633.538l.132 3.196z" fill={selectedGateway === 'paypal' ? '#fff' : '#003087'}/>
                        <path d="M23.048 7.667c-.03.196-.067.398-.112.605-.904 4.643-3.998 6.245-7.95 6.245h-2.01a.977.977 0 0 0-.965.828l-1.03 6.523-.292 1.85a.515.515 0 0 0 .509.596h3.573a.856.856 0 0 0 .846-.724l.035-.18.67-4.243.043-.234a.856.856 0 0 1 .846-.724h.532c3.448 0 6.149-1.4 6.938-5.452.33-1.693.159-3.107-.714-4.1a3.407 3.407 0 0 0-.919-.79z" fill={selectedGateway === 'paypal' ? '#dbeafe' : '#0070BA'}/>
                      </svg>
                    ) : gw.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`font-bold text-sm ${selectedGateway === gw.id ? 'text-white' : 'text-gray-900'}`}>
                      {gw.label}
                    </div>
                    <div className={`text-[11px] ${selectedGateway === gw.id ? 'text-white/70' : 'text-gray-500'}`}>
                      {gw.description}
                    </div>
                  </div>
                  {selectedGateway === gw.id && (
                    <Icon name="CheckCircle" size={20} className="text-white/90 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>

            {/* Config status notice */}
            {!PAYMENT_CONFIG[selectedGateway]?.paymentLink &&
             !PAYMENT_CONFIG[selectedGateway]?.revolutMe && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-700 flex items-start gap-2">
                <Icon name="AlertTriangle" size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                  Payment link not configured yet. Click "Pay Now" to simulate the donation flow.
                  <br /><strong className="text-amber-800">Owner:</strong> Add your credentials in <code className="bg-amber-100 px-1 rounded">DonateModal.jsx → PAYMENT_CONFIG</code>
                </span>
              </div>
            )}

            {/* Pay button */}
            <button
              onClick={handlePayNow}
              className={`w-full py-3.5 bg-gradient-to-r ${GATEWAYS.find(g => g.id === selectedGateway)?.color || 'from-emerald-500 to-cyan-600'} text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 text-sm`}
            >
              {GATEWAYS.find(g => g.id === selectedGateway)?.icon}
              <span>Pay ${effectiveAmount.toFixed(2)} with {GATEWAYS.find(g => g.id === selectedGateway)?.label}</span>
            </button>

            {/* Trust */}
            <div className="flex items-center justify-center gap-4 mt-3 text-[9px] text-gray-400">
              <span className="flex items-center gap-1"><Icon name="Shield" size={10} className="text-green-400" /> Encrypted</span>
              <span className="flex items-center gap-1"><Icon name="Lock" size={10} className="text-blue-400" /> Secure</span>
            </div>
          </div>
        )}

        {/* ═══ STEP 3: Waiting for provider webhook confirmation ═══ */}
        {step === 'waiting' && (
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <Icon
                name={waitingStatus === 'timeout' ? 'Clock' : 'Loader2'}
                size={28}
                className={`text-blue-600 ${waitingStatus === 'polling' ? 'animate-spin' : ''}`}
              />
            </div>
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">
              {waitingStatus === 'timeout' ? 'Still waiting on payment' : 'Confirming your payment'}
            </h2>
            <p className="text-sm text-gray-600 mb-1">
              A secure payment page opened for{' '}
              <span className="font-bold text-emerald-600">${effectiveAmount.toFixed(2)}</span>
              {' '}via {GATEWAYS.find(g => g.id === selectedGateway)?.label}.
            </p>
            <p className="text-xs text-gray-400 mb-4">
              {waitingStatus === 'timeout'
                ? 'We have not received a verified payment notification yet. The meter updates automatically when Stripe, PayPal, or Revolut confirms a charge.'
                : 'We only count donations after the payment provider confirms the charge. This screen watches the monthly meter for an update.'}
            </p>

            <ServerCostMeter
              goal={fundingStats.goal}
              raised={fundingStats.raised}
              loading={false}
              month={fundingStats.month}
            />

            {waitingStatus === 'polling' && (
              <p className="text-xs text-cyan-700 bg-cyan-50 border border-cyan-100 rounded-lg px-3 py-2 mb-4">
                Listening for a verified payment on the meter…
              </p>
            )}

            {waitingStatus === 'timeout' && (
              <button
                onClick={handleKeepChecking}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-sm rounded-xl shadow hover:shadow-lg transition-all flex items-center justify-center gap-2 mb-3"
              >
                <Icon name="RefreshCw" size={16} />
                Keep checking
              </button>
            )}

            <button
              onClick={() => setStep('pay')}
              className="w-full py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-colors mb-1"
            >
              I didn&apos;t pay — go back
            </button>
            <button
              onClick={onClose}
              className="w-full py-2.5 text-sm font-medium text-gray-400 hover:text-gray-700 rounded-xl transition-colors"
            >
              Close — meter will update when payment clears
            </button>
          </div>
        )}

        {/* ═══ STEP 4: Success + Brag ═══ */}
        {step === 'success' && (
          <div className="p-6 text-center">
            {/* Success animation */}
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <Icon name="Heart" size={28} className="text-emerald-600 animate-pulse" />
            </div>

            <h2 className="font-heading text-xl font-bold text-gray-900 mb-1">
              Thank You! 🎉
            </h2>
            <p className="text-sm text-gray-600 mb-1">
              Your <span className="font-bold text-emerald-600">${effectiveAmount.toFixed(2)}</span> helps keep transparency alive.
            </p>
            <p className="text-xs text-gray-400 mb-5">
              Every dollar keeps the servers running and the data fresh.
            </p>

            {/* Share on social media */}
            <div className="bg-gray-50 rounded-xl p-4 text-left mb-4 border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">📣</span>
                <h3 className="font-bold text-sm text-gray-900">Share on Social Media</h3>
              </div>

              <p className="text-xs text-gray-500 mb-3">
                Select platforms and hit "Share Now"!
              </p>

              {/* Platform chips */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {SHARE_PLATFORMS.map(p => {
                  const isSelected = selectedPlatforms.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => togglePlatform(p.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isSelected
                          ? `${p.color} text-white shadow-sm`
                          : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>

              {/* Preview */}
              <div className="bg-white rounded-lg p-2.5 border border-gray-100 mb-3">
                <p className="text-[11px] text-gray-600 leading-relaxed">
                  I just donated <strong>${effectiveAmount.toFixed(2)}</strong> to keep <strong>@WhoYouMakinRich</strong> running — a free platform exposing who's really making money. 💰🔍
                </p>
              </div>

              <button
                onClick={handleShareAll}
                disabled={selectedPlatforms.length === 0}
                className="w-full py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold text-sm rounded-lg shadow hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Icon name="Share2" size={14} />
                Share on {selectedPlatforms.length} Platform{selectedPlatforms.length !== 1 ? 's' : ''}
              </button>
            </div>

            {/* Merch upsell */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 text-center mb-4 border border-amber-100">
              <p className="text-sm font-semibold text-gray-900 mb-1">
                Want to support us even more? 👕
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Grab a <strong>$WYMR?</strong> tee — 100% of profits fund the platform
              </p>
              <a
                href={MERCH_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold text-sm rounded-lg shadow hover:shadow-lg transition-all"
              >
                <Icon name="ShoppingBag" size={14} />
                Browse Merch Store
              </a>
            </div>

            {/* Close */}
            <button
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-primary transition-colors flex items-center gap-1.5 mx-auto"
            >
              <Icon name="ArrowLeft" size={13} />
              Back to browsing
            </button>
          </div>
        )}

        {/* ═══ STEP: Merch (focusMerch) ═══ */}
        {step === 'merch' && (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-3">
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
              >
                <Icon name="X" size={14} />
              </button>
              <div>
                <h2 className="font-heading text-lg font-bold text-gray-900">
                  🛍️ Merch Store
                </h2>
              </div>
            </div>

            <MerchProfitsNotice />

            <ServerCostMeter
              goal={fundingStats.goal}
              raised={fundingStats.raised}
              loading={fundingLoading}
              month={fundingStats.month}
            />

            {/* Swipeable product carousel */}
            <ProductCarousel products={MERCH_PRODUCTS} />

            {/* Merch CTA button — orange gradient */}
            <a
              href={MERCH_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:from-amber-400 hover:to-orange-500 transition-all flex items-center justify-center gap-2 text-sm"
            >
              <Icon name="ShoppingBag" size={16} />
              Grab Some Merch
            </a>

            <p className="text-center text-[11px] text-gray-400 mt-3">
              Both help keep the servers and services running 🙌🤟🙏
            </p>

            {/* ── "Or" divider ── */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Donate CTA */}
            <button
              onClick={() => setStep('amount')}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 text-sm"
            >
              <Icon name="Heart" size={16} />
              Make a Donation Instead
            </button>
          </div>
        )}
      </div>

      {/* CSS animation */}
      <style>{`
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes modalSlideUpBottom {
          from { opacity: 0; transform: translateY(100%); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes merchMarquee {
          from { transform: translateX(0); }
          to { transform: translateX(var(--marquee-distance, -50%)); }
        }
        .merch-marquee-track {
          animation: merchMarquee var(--marquee-duration, 18s) linear infinite;
          will-change: transform;
        }
        .merch-marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default DonateModal;
